
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';

import {
  FormBuilder,
  FormControl,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

import Swal from 'sweetalert2';

import { Country } from '../leads-signup/services/leads-signup-service.service';
import {
  ServiceItem,
  ServicesQuoteService
} from './services-quote/services-quote.service';
import { Meta, Title } from '@angular/platform-browser';
import { CanonicalService } from '../../shared/seo/canonical.service';

@Component({
  selector: 'app-quote-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgxMatSelectSearchModule
  ],
  templateUrl: './quote-form.component.html',
  styleUrl: './quote-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class QuoteFormComponent implements OnInit {
  @ViewChild(FormGroupDirective)
  private formDirective!: FormGroupDirective;

  utm_source = '';
utm_medium = '';
utm_campaign = '';
utm_content = '';
  isSubmitting = false;

  countries: Country[] = [];
  services: ServiceItem[] = [];

  filteredCountries: Country[] = [];
  filteredServices: ServiceItem[] = [];

  countrySearch = new FormControl('');
  serviceSearch = new FormControl('');

  form: ReturnType<FormBuilder['group']>;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private canonicalService: CanonicalService,
    private fb: FormBuilder,
    private api: ServicesQuoteService,
     private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      full_name: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z]+(?: [a-zA-Z]+)*$/)
        ]
      ],
      business_email: [
        '',
        [
          Validators.required,
          Validators.email
        ]
      ],
      phone_number: [
        '',
        [
          Validators.required,
         Validators.pattern(/^[0-9]{8,15}$/)
        ]
      ],
      company_name: [''],
      country: [
        '',
        Validators.required
      ],
      type_of_service: [
        '',
        Validators.required
      ]
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle(
      'Discuss Your Project | MALtech'
    );

    // Meta description
    this.metaService.updateTag({
      name: 'description',
      content:
        'Discuss your project with MALtech. Share your requirements for software development, AI and machine learning, cloud and DevOps, digital security, and email solutions. Our team will review your inquiry and get back to you.',
    });

    // Meta keywords
    this.metaService.updateTag({
      name: 'keywords',
      content:
        'Discuss Project, Software Development, AI and Machine Learning, Cloud and DevOps, Digital Security, Software Consulting, Digital Marketing, UI UX Design, Enterprise Solutions, Transactional Emails, Marketing Emails, MALtech',
    });

    // Robots
    this.metaService.updateTag({
      name: 'robots',
      content: 'index, follow',
    });

    // Canonical URL
    this.canonicalService.setCanonicalURL(
      'discuss-project/'
    );
    this.loadServices();
    this.loadCountries();
    this.setupSearchFilters();

      // Read UTM parameters from the browser URL
  this.route.queryParamMap.subscribe(params => {
    this.utm_source = params.get('utm_source') || '';
    this.utm_medium = params.get('utm_medium') || '';
    this.utm_campaign = params.get('utm_campaign') || '';
    this.utm_content = params.get('utm_content') || '';
  });
  }

  // Load services from the existing API
  private loadServices(): void {
    this.api.getServices().subscribe({
      next: res => {
        this.services = res.data || [];

        this.filteredServices = [...this.services];
      },
      error: () => {
        this.showError('Failed to load services.');
      }
    });
  }

  // Load countries from the existing API
  private loadCountries(): void {
    this.api.getCountries().subscribe({
      next: res => {
        this.countries = res || [];
        this.filteredCountries = [...this.countries];
      },
      error: () => {
        this.showError('Failed to load countries.');
      }
    });
  }

  // Static Services and Products displayed on the left side
// Static Services and Products displayed on the left side
sources = [
  {
    title: 'Our Services',
    items: [
      'Software Development',
      'AI & Machine Learning',
      'Cloud & DevOps',
      'Digital Security',
      'Software Consulting',
      'Digital Marketing',
      'UI/UX Design',
      'Enterprise Solutions'
    ]
  },
  {
    title: 'Our Products',
    items: [
      'Transactional Emails',
      'Marketing Emails'
    ]
  }
];

  // Search filters
  private setupSearchFilters(): void {
    this.countrySearch.valueChanges.subscribe(value => {
      this.filterCountries(value || '');
    });

    this.serviceSearch.valueChanges.subscribe(value => {
      this.filterServices(value || '');
    });
  }

  private filterCountries(value: string): void {
    const query = value.toLowerCase().trim();

    this.filteredCountries = this.countries.filter(country =>
      country.name.toLowerCase().includes(query)
    );
  }

  private filterServices(value: string): void {
    const query = value.toLowerCase().trim();

    this.filteredServices = this.services.filter(service =>
      service.service_name.toLowerCase().includes(query)
    );
  }

  // Prevent < and > from being typed
  preventAngleBrackets(event: KeyboardEvent): void {
    if (event.key === '<' || event.key === '>') {
      event.preventDefault();
    }
  }

  // Submit quote request
  submit(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const v = this.form.getRawValue();


    this.api.submitQuote({
      full_name: v.full_name!.trim(),
      business_email: v.business_email!.trim(),
      phone_number: v.phone_number!.trim(),
      company_name: v.company_name?.trim() || null,
      country: v.country!,
      type_of_service: v.type_of_service!
    }, {
      utm_source: this.utm_source,
      utm_medium: this.utm_medium,
      utm_campaign: this.utm_campaign,
      utm_content: this.utm_content
    })
.subscribe({
      

      next: () => {
        this.isSubmitting = false;

        // Reset values, touched/dirty states, and submitted state
        this.formDirective.resetForm({
          full_name: '',
          business_email: '',
          phone_number: '',
          company_name: '',
          country: null,
          type_of_service: null
        });

        // Clear dropdown search fields
        this.countrySearch.setValue('');
        this.serviceSearch.setValue('');

        Swal.fire({
          icon: 'success',
          title: 'Quote Request Sent!',
          text: 'Thank you. Our team will contact you shortly.',
          confirmButtonColor: '#009fe4'
        });
      },



      error: () => {
        this.isSubmitting = false;

        this.showError(
          'Unable to submit your request. Please try again.'
        );
      }
    });
  }

  private showError(text: string): void {
    Swal.fire({
      icon: 'warning',
      title: 'Error',
      text,
      confirmButtonColor: '#009fe4'
    });
  }
}
